import { useEffect } from "react";
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_TWITTER_CARD,
  SITE_NAME,
  buildCanonicalUrl,
} from "@/config/siteSeo";

interface SEOOptions {
  title: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  robots?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  // Open Graph options (fallback to title/description when omitted)
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  // Twitter card options (fallback to Open Graph / title/description)
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  siteName?: string;
  ogUrl?: string;
  twitterSite?: string;
  schemaId?: string;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonicalPath,
  robots,
  jsonLd,
  ogTitle,
  ogDescription,
  ogType,
  ogImage,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  siteName,
  ogUrl,
  twitterSite,
  schemaId,
}: SEOOptions) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const ensureMeta = (name: string, content: string | undefined) => {
      if (!content) return;
      let element = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
      if (!element) {
        element = document.createElement("meta");
        element.name = name;
        document.head.appendChild(element);
      }
      element.content = content;
    };

    const ensureMetaProperty = (property: string, content: string | undefined) => {
      if (!content) return;
      let element = document.querySelector<HTMLMetaElement>(`meta[property='${property}']`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    ensureMeta("description", description);
    ensureMeta("keywords", keywords);
    ensureMeta("robots", robots);

    if (canonicalPath) {
      const href = buildCanonicalUrl(canonicalPath);
      let linkEl = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
      if (!linkEl) {
        linkEl = document.createElement("link");
        linkEl.rel = "canonical";
        document.head.appendChild(linkEl);
      }
      linkEl.href = href;
    }

    // Open Graph (fallbacks)
    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDescription = ogDescription || description;
    const resolvedOgType = ogType || "website";
    const resolvedOgUrl = ogUrl || (canonicalPath ? buildCanonicalUrl(canonicalPath) : window.location.href);

    ensureMetaProperty("og:title", resolvedOgTitle);
    ensureMetaProperty("og:description", resolvedOgDescription);
    ensureMetaProperty("og:type", resolvedOgType);
    ensureMetaProperty("og:url", resolvedOgUrl);
    ensureMetaProperty("og:image", ogImage || DEFAULT_OG_IMAGE);
    ensureMetaProperty("og:site_name", siteName || SITE_NAME);

    // Twitter card (fallbacks)
    const resolvedTwitterTitle = twitterTitle || resolvedOgTitle;
    const resolvedTwitterDescription = twitterDescription || resolvedOgDescription;

    ensureMeta("twitter:card", twitterCard || DEFAULT_TWITTER_CARD);
    ensureMeta("twitter:title", resolvedTwitterTitle);
    ensureMeta("twitter:description", resolvedTwitterDescription);
    ensureMeta("twitter:image", twitterImage || ogImage || DEFAULT_OG_IMAGE);
    ensureMeta("twitter:site", twitterSite || "@pdfworkspace");

    // Structured data (JSON-LD)
    const scriptId = schemaId || "PDFWorkSpace-structured-data";
    let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (jsonLd) {
      const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = scriptId;
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(payload);
    } else if (scriptEl) {
      scriptEl.remove();
    }
  }, [
    title,
    description,
    keywords,
    canonicalPath,
    robots,
    jsonLd,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    siteName,
    ogUrl,
    twitterSite,
    schemaId,
  ]);
};
